<script lang="ts">
    import { onMount } from "svelte";

    import SVGIcon from "$/components/SVGIcon.svelte";

    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import { checkAndGenerateSocialMediaURL } from "$/utils/Utils";
    import { CardTitleUtils } from "../card/CardTitle/CardTitleUtils";
    import { AppStore } from "$/stores/AppStore";
    
    import { v4 as uuid } from "uuid";
    import {ExtensionStore} from "$/stores/ExtensionStore";

    let saveButton: HTMLButtonElement;
    let observer: MutationObserver;
    let transcriptObserver: MutationObserver;
    let initialLoad = false;
    export let editRole: number;

    // function to create the "Save Post" button
    const createSaveButton = (transcriptElement?: HTMLElement) => {
        // clone our button
        const videoSaveButton = saveButton.cloneNode(true) as HTMLButtonElement;

        // remove the hidden class from the button
        videoSaveButton.classList.remove("cw-save-hide");

        videoSaveButton.addEventListener("click", async () => {
            // find .loading-spinner and show it
            let loadingSpinner = videoSaveButton.querySelector(
                ".cw-save__loading-spinner",
            ) as HTMLElement;
            let saveIcon = videoSaveButton.querySelector(
                ".cw-save__icon",
            ) as HTMLElement;
            loadingSpinner.style.display = "block";
            saveIcon.style.display = "none";
            videoSaveButton.style.pointerEvents = "none";

            // handle saving the video image as a snippet
            let player = document.querySelector(
                "video.video-stream.html5-main-video",
            ) as HTMLVideoElement;
            let url = await generateYoutubeShareTimeLink(
                window.location.href,
                player,
            );
            let currentTimeInMinutesAndSec = `${Math.floor(player.currentTime / 60)}:${Math.floor(player.currentTime % 60)}`;
            let image = await generateYoutubeImage(player);
            let content = `<img src="${image}" alt="${window.document.title}: ${currentTimeInMinutesAndSec}">`;
            let titleSuggestion = CardTitleUtils.generateTitle(document) ?? "";

            let transcriptText = transcriptElement
                ? await generateTranscriptText(transcriptElement)
                : null;
            if (transcriptText) content = `<p>${transcriptText}</p>`;

            let snippet = {
                id: uuid(),
                content: content ?? "No content",
                nameSuggestion: titleSuggestion,
                comments: [],
                url: checkAndGenerateSocialMediaURL("youtube", url),
                createdBy: $TokenStore?.user,
                createdOn: new Date().getTime(),
            };

            ExtensionStore.setOpened(true);

            ActiveTopicStore.createSnippet(snippet);

            // set focus on the newly created snippet
            AppStore.setFocused({ snippetId: snippet.id });

            // hide the loading spinner
            loadingSpinner.style.display = "none";
            videoSaveButton.style.pointerEvents = "auto";
            saveIcon.style.display = "block";
        });

        return videoSaveButton;
    };

    // function to add "Save Post" buttons to posts
    const addSaveButtonToPosts = () => {
        const ytRightControls = document.querySelector(
            ".ytp-right-controls",
        ) as HTMLElement;
        // check if the post already has a "Save Post" button
        if (!ytRightControls?.querySelector(".cw-save-btn")) {
            const saveButton = createSaveButton();

            // prepend the save button to the post
            ytRightControls?.prepend(saveButton);
        }
    };

    const generateYoutubeShareTimeLink = async (
        url: String,
        player: HTMLVideoElement,
    ) => {
        // youtube does not create links for sharing live videos with timestamp
        // but we will save the timestamp in the url anyway
        // so maybe if the video is live, the user can still go to the video
        // and then if live is over, they can go to the timestamp in the video
        let playedTime = player.currentTime;
        let timeInSeconds = Math.floor(playedTime);

        // first remove any existing time query params from the url
        // then add the new time query param
        let urlWithTime = url.replace(/&t=\d+/g, "") + `&t=${timeInSeconds}s`;

        return urlWithTime;
    };

    const generateYoutubeImage = async (videoElement: HTMLVideoElement) => {
        // Create a canvas element
        var canvas = document.createElement("canvas");
        var ctx = canvas.getContext("2d");

        // Change the size here
        canvas.width = videoElement.offsetWidth;
        canvas.height = videoElement.offsetHeight;
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        return canvas.toDataURL("image/jpeg", 1.0);
    };

    const generateTranscriptText = async (element: HTMLElement) => {
        const nextSiblings = (el: HTMLElement, n: number) => {
            let siblings = [];
            while (el && n) {
                el = el.nextElementSibling as HTMLElement;
                // if el has timestamp, add to siblings array else its not a transcript segment
                if (el.querySelector(".segment-timestamp")) {
                    siblings.push(el);
                    n--;
                }
            }
            return siblings;
        };

        let siblings = nextSiblings(element.parentElement, 8);
        let within15Seconds = [element];

        let initialTimestamp = element.querySelector(
            ".segment-timestamp",
        ) as HTMLElement;
        let initialTimeInSeconds =
            parseInt(initialTimestamp.innerText.split(":")[0]) * 60 +
            parseInt(initialTimestamp.innerText.split(":")[1]);

        // loop through the next 8 siblings and remove sibling if the timestamp is outside of 15 seconds of the initial timestamp
        siblings.forEach((sibling) => {
            let timestamp = sibling.querySelector(
                ".segment-timestamp",
            ) as HTMLElement;
            let timeInSeconds =
                parseInt(timestamp.innerText.split(":")[0]) * 60 +
                parseInt(timestamp.innerText.split(":")[1]);
            if (Math.abs(timeInSeconds - initialTimeInSeconds) <= 15) {
                within15Seconds.push(sibling);
            }
        });

        // clone siblings, remove segment-timestamp element and return the innerText
        let clones = within15Seconds.map(
            (sibling) => sibling.cloneNode(true) as HTMLElement,
        );

        clones.forEach((clone) => {
            let timestamp = clone.querySelector(".segment-timestamp");
            if (timestamp) timestamp.remove();
        });

        let transcript = clones.map((clone) => clone.innerText).join("<br>");

        // words to remove from transcript
        let wordsToRemove = [
            "[Music]",
            "[Applause]",
            "[Laughter]",
            "[Music Ends]",
            "[Applause Ends]",
            "[Laughter Ends]",
        ];
        wordsToRemove.forEach(
            (word) => (transcript = transcript.replace(word, "")),
        );

        return transcript;
    };

    // function to check the current URL and add Save Post buttons if needed
    const checkAndAddSaveButtons = () => {
        const currentUrl = window.location.href;
        if (currentUrl.includes("youtube.com")) {
            if (
                !document.querySelector(".html5-video-player") ||
                !document.querySelector(".ytp-right-controls")
            ) {
                setTimeout(() => {
                    addSaveButtonToPosts();
                }, 1000);
            } else {
                addSaveButtonToPosts();
            }
        }

        // continue observing changes in the document body
        observer?.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
        transcriptObserver?.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    };

    // function to remove all "Save Post" buttons
    const removeSaveButtons = () => {
        const saveButtons = document.querySelectorAll(".cw-save-btn");
        saveButtons.forEach((button: HTMLElement) => button.remove());

        // stop observing changes in the document body if observer exists
        observer?.disconnect();
        transcriptObserver?.disconnect();
    };

    const checkAndAddSaveButtonsToTranscriptTimestamps = () => {
        let transcriptTimestamps = document.querySelectorAll(
            "ytd-transcript-segment-renderer",
        ) as NodeListOf<HTMLElement>;
        transcriptTimestamps.forEach((timestamp: HTMLElement) => {
            let container = timestamp.querySelector(
                ".ytd-transcript-segment-renderer",
            ) as HTMLElement;
            let timestampButton = timestamp.querySelector(".cw-save-btn");
            if (!timestampButton) {
                let saveButton = createSaveButton(container);
                saveButton.classList.add("cw-ytd-transcript-segment-btn");
                container.prepend(saveButton);
            }
        });
    };

    onMount(() => {
        // create observer listener for new elements with the class "html5-video-player" and id "movie_player"
        observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" && editRole >= 3) {
                    mutation.addedNodes.forEach((addedNode) => {
                        if (
                            addedNode instanceof HTMLElement &&
                            addedNode.classList.contains(
                                "html5-video-player",
                            ) &&
                            addedNode.id === "movie_player"
                        ) {
                            addSaveButtonToPosts();
                        }
                    });
                }
            });
        });

        // create observer listener for new elements with ytd-transcript-segment-list-renderer class
        transcriptObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" && editRole >= 3) {
                    mutation.addedNodes.forEach((addedNode) => {
                        // if added node is ytd-transcript-search-panel-renderer
                        if (
                            addedNode instanceof HTMLElement &&
                            addedNode.tagName ===
                                "YTD-TRANSCRIPT-SEGMENT-LIST-RENDERER"
                        ) {
                            checkAndAddSaveButtonsToTranscriptTimestamps();
                        }
                    });
                }
            });
        });

        // initial check for posts on page load
        if (editRole >= 3) {
            checkAndAddSaveButtons();
        }

        initialLoad = true;
    });

    // if user has edit role >= 3 and initial load (component mounted), add "Save Post" buttons else remove them
    $: if (initialLoad && editRole >= 3) {
        checkAndAddSaveButtons();
        checkAndAddSaveButtonsToTranscriptTimestamps();
    } else {
        removeSaveButtons();
    }
</script>

{#if window.location.href.includes("youtube.com")}
    <button
        class="cw-save-hide ytp-play-button ytp-button cw-save-btn"
        title="Save as Snippet"
        bind:this={saveButton}
    >
        <div class="cw-save__loading-spinner ytd" style="display: none;">
            <SVGIcon
                name="spinner"
                color="#efedec"
                height="100%"
                size="100%"
                viewbox="0 0 24 24"
                strokeWidth="2"
            />
        </div>
        <div class="cw-save__icon ytd">
            <SVGIcon
                name="save"
                color="#efedec"
                height="100%"
                size="100%"
                viewbox="-5 -5 38 38"
                strokeWidth="0.5"
            />
        </div>
    </button>
{/if}

<style>
    @import "../../extension/external.css";
</style>
